import { useMemo, useEffect } from "react";
import { ClickPopover } from "src/components/ui-components/ClickPopover/ClickPopover";
import { Icon } from "src/components/icon/icon";
import { VisualItem } from "src/components/visual/visual";
import { ExecutionSucceeded } from "src/gql/codegen/graphql";
import viewStyles from "../view.module.css";
import styles from "./TablePreviewButton.module.css";
import { useTablePreview } from "./useTablePreview";
import AmplitudeService from "src/services/amplitude";

export default function TablePreviewButton(props: { fullName: string }) {
  return (
    <ClickPopover
      position="below-align-left"
      content={() => (
        <div className={styles.popup}>
          <TablePreview {...props} />
        </div>
      )}
    >
      <button className={viewStyles.dataExplorerIconButton}>
        <Icon icon="eye" />
      </button>
    </ClickPopover>
  );
}

function TablePreview(props: { fullName: string }) {
  const escapedFullName = escapeFullName(props.fullName);
  const tablePreview = useTablePreview(escapedFullName);

  useEffect(() => {
    AmplitudeService.trackEvent("DataExplorer Table Preview", {
      table: props.fullName,
    });
  }, []);

  if (tablePreview.state === "loading") {
    return <div className={styles.message}>Loading table preview...</div>;
  }

  if (tablePreview.state === "error") {
    return (
      <div className={styles.message}>
        An error occurred while loading the table preview
      </div>
    );
  }

  const { queryId, execution } = tablePreview.data;

  if (execution.__typename === "ExecutionFailed") {
    return (
      <div className={styles.message}>
        An error occurred while loading the table preview
      </div>
    );
  }

  return (
    <TablePreviewResults
      table={props.fullName}
      queryId={queryId}
      execution={execution}
    />
  );
}

function TablePreviewResults(props: {
  table: string;
  queryId: number;
  execution: ExecutionSucceeded;
}) {
  const columns = useMemo(() => {
    return props.execution.columns?.map((column) => ({ name: column })) ?? [];
  }, [props.execution]);

  const rows = (props.execution.data as any[]) ?? [];

  if (rows.length === 0) {
    return <div className={styles.message}>This table has no data</div>;
  }

  return (
    <VisualItem
      className={styles.visual}
      queryName={props.table}
      visual={{
        type: "table",
        id: props.queryId,
        name: "Table preview",
        options: {},
      }}
      result={{
        matrix: {
          columns,
          rows,
        },
      }}
      watermark={false}
      search={false}
    />
  );
}

// ethereum.blocks -> "ethereum"."blocks"
function escapeFullName(table: string) {
  return table
    .split(".")
    .map((s) => `"${s}"`)
    .join(".");
}
